import { z } from 'zod'
import type { PhotoStore } from '../../application/ports.ts'
import type { PhotoId } from '../../domain/ids.ts'
import { ZipIlegible, unzipStore, zipStore } from './zip.ts'

/**
 * Las fotos, en su propio archivo.
 *
 * Van aparte del registro y no dentro por dos razones. Una de tamaño: el
 * registro de un año son unos cientos de kilobytes y se puede exportar cada
 * semana sin pensar, mientras que las fotos son megas — meterlas dentro
 * convertiría la copia semanal en algo que da pereza hacer, y una copia que da
 * pereza no se hace. Y otra de forma: el registro es texto que se lee con
 * cualquier editor, y meterle fotos dentro obligaría a codificarlas en base64,
 * que abulta un tercio más y deja de ser legible.
 *
 * El `id` de cada foto es lo único que importa de verdad: el registro no guarda
 * fotos, guarda ids. Una foto restaurada con otro id es una foto huérfana, así
 * que el manifiesto los lleva y la importación los respeta.
 */

const KIND = z.enum(['reference', 'result', 'check'])

const ManifiestoSchema = z.object({
  app: z.literal('mision-hogar-fotos'),
  schemaVersion: z.literal(1),
  exportedAt: z.string(),
  photos: z.array(
    z.object({
      id: z.string().min(1),
      name: z.string().min(1),
      kind: KIND,
      createdAt: z.string(),
      type: z.string(),
    }),
  ),
})

const MANIFIESTO = 'manifiesto.json'

/** La extensión importa: es lo que hace que la foto se abra al extraerla. */
function extensionDe(type: string): string {
  if (type === 'image/jpeg') return 'jpg'
  if (type === 'image/png') return 'png'
  if (type === 'image/webp') return 'webp'
  if (type === 'image/avif') return 'avif'
  return 'bin'
}

export interface ExportacionDeFotos {
  readonly blob: Blob
  readonly cuantas: number
  readonly bytes: number
}

/**
 * Escribe el archivo de fotos. Las que no estén —una que se borró entre el
 * listado y la lectura— se saltan en silencio: es una copia de seguridad, no una
 * comprobación de integridad, y pararla entera por una foto que ya no está sería
 * dejar al padre sin las otras doscientas.
 */
export async function exportarFotos(store: PhotoStore, now: Date): Promise<ExportacionDeFotos> {
  const listado = await store.list()
  const entradas: { name: string; blob: Blob; at: string }[] = []
  const fichas: z.infer<typeof ManifiestoSchema>['photos'] = []

  for (const foto of listado) {
    const blob = await store.get(foto.id)
    if (!blob) continue
    const name = `fotos/${foto.id}.${extensionDe(blob.type)}`
    entradas.push({ name, blob, at: foto.createdAt })
    fichas.push({
      id: foto.id,
      name,
      kind: foto.kind,
      createdAt: foto.createdAt,
      type: blob.type,
    })
  }

  const manifiesto: z.infer<typeof ManifiestoSchema> = {
    app: 'mision-hogar-fotos',
    schemaVersion: 1,
    exportedAt: now.toISOString(),
    photos: fichas,
  }

  const blob = await zipStore([
    {
      name: MANIFIESTO,
      blob: new Blob([JSON.stringify(manifiesto, null, 2)], { type: 'application/json' }),
      at: now.toISOString(),
    },
    ...entradas,
  ])

  return { blob, cuantas: entradas.length, bytes: blob.size }
}

export interface ImportacionDeFotos {
  readonly puestas: number
  readonly yaEstaban: number
  /** En el manifiesto pero no en el archivo. */
  readonly perdidas: number
}

/**
 * Devuelve las fotos a su sitio, cada una con el id que el registro ya usa.
 *
 * Se exige el manifiesto. Un zip de fotos cualquiera —descargado, hecho a mano—
 * no vale para nada aquí: sin el manifiesto no hay ids, y sin ids las fotos no
 * las reclama ninguna tarea. Antes decirlo que meter doscientos archivos que no
 * se van a ver nunca.
 */
export async function importarFotos(store: PhotoStore, file: Blob): Promise<ImportacionDeFotos> {
  const entradas = await unzipStore(file)
  const manifiesto = entradas.find((e) => e.name === MANIFIESTO)
  if (!manifiesto) {
    throw new ZipIlegible('Ese archivo no es una copia de las fotos de esta app.')
  }

  let crudo: unknown
  try {
    crudo = JSON.parse(new TextDecoder().decode(manifiesto.bytes))
  } catch {
    throw new ZipIlegible('El índice de las fotos no se entiende.')
  }
  const leido = ManifiestoSchema.safeParse(crudo)
  if (!leido.success) {
    throw new ZipIlegible('El índice de las fotos no es de esta app.')
  }

  const porNombre = new Map(entradas.map((e) => [e.name, e.bytes]))
  let puestas = 0
  let yaEstaban = 0
  let perdidas = 0

  for (const ficha of leido.data.photos) {
    const bytes = porNombre.get(ficha.name)
    if (!bytes) {
      perdidas += 1
      continue
    }
    // Sin volver a reducirla: ya se redujo cuando se guardó por primera vez, y
    // recodificarla aquí solo le quitaría calidad a cada copia.
    const resultado = await store.restore({
      id: ficha.id as PhotoId,
      blob: new Blob([bytes], { type: ficha.type }),
      kind: ficha.kind,
      createdAt: ficha.createdAt,
    })
    if (resultado === 'imported') puestas += 1
    else yaEstaban += 1
  }

  return { puestas, yaEstaban, perdidas }
}
